import { runtimeConfig } from 'config/runtimeConfig';
import ServiceWrapper from 'Services/ServiceWrapper';
import AuthManager from 'Common/AuthManager';
import { AdaptersResponseData, AssignmentPayloadEntry, AssignmentsSaveResult } from '../types';

/**
 * Both calls go to the GMS backend, not to the Midway connector directly (unlike most of the
 * Connectors screen): the party list, the party → channel-key resolution and the `GMSAdapter`
 * RBAC check all live in GMS. See the backend module doc for the response shapes.
 */
const GMS_BACKEND_URL = runtimeConfig.GMS_BACKEND_API_URL;

export type ServiceResult<T> = { success: true; data: T } | { success: false; error: string };

const errorMessage = (error: unknown, fallback: string): string =>
  error instanceof Error && error.message ? error.message : fallback;

export const fetchAdapters = async (): Promise<ServiceResult<AdaptersResponseData>> => {
  try {
    const response = await ServiceWrapper.doGet(
      `${GMS_BACKEND_URL}connector/adapters`,
      null,
      null,
      AuthManager.getGmsToken(),
    );
    if (response?.errorfound || !response?.data?.done || !response.data.data) {
      return { success: false, error: response?.data?.message || 'Failed to load adapters' };
    }
    return { success: true, data: response.data.data as AdaptersResponseData };
  } catch (error) {
    return { success: false, error: errorMessage(error, 'Failed to load adapters') };
  }
};

export const saveAssignments = async (
  assignments: AssignmentPayloadEntry[],
): Promise<ServiceResult<AssignmentsSaveResult>> => {
  try {
    const response = await ServiceWrapper.doPut(
      `${GMS_BACKEND_URL}connector/adapters/assignments`,
      { assignments },
      null,
      AuthManager.getGmsToken(),
    );
    if (response?.errorfound || !response?.data?.done) {
      return { success: false, error: response?.data?.message || 'Failed to save adapter assignments' };
    }
    return { success: true, data: (response.data.data ?? { added: 0, removed: 0 }) as AssignmentsSaveResult };
  } catch (error) {
    return { success: false, error: errorMessage(error, 'Failed to save adapter assignments') };
  }
};
